import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { guestsApi } from '../services/api';
import {
  Star,
  AlertTriangle,
  Heart,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import clsx from 'clsx';

interface GuestRecognitionProps {
  guestId: string;
  guestName?: string;
  onClose?: () => void;
  compact?: boolean;
}

interface Suggestion {
  menuItemId: string;
  name: string;
  priceCents: number;
  categoryName: string | null;
  score?: number;
  reason?: string;
  reasons?: string[];
}

export default function GuestRecognition({
  guestId,
  guestName,
  onClose,
  compact = false,
}: GuestRecognitionProps) {
  const [expanded, setExpanded] = useState(!compact);

  // Fetch guest suggestions
  const { data, isLoading, error } = useQuery({
    queryKey: ['guest-suggestions', guestId],
    queryFn: async () => {
      const response = await guestsApi.getSuggestions(guestId);
      return response.data;
    },
    enabled: !!guestId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  if (isLoading) {
    return (
      <div className={clsx(
        'bg-primary-50 border border-primary-100 animate-pulse',
        compact ? 'rounded-lg p-3' : 'rounded-xl p-4'
      )}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-primary-100 rounded w-32" />
            <div className="h-3 bg-primary-100 rounded w-24" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const { allergies = [], suggestions = [], avoidItems = [], totalVisits = 0 } = data;
  const hasAllergies = allergies.length > 0;
  const hasSuggestions = suggestions.length > 0;

  // Compact banner mode
  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full bg-gradient-to-r from-primary-50 to-amber-50 border border-primary-100 rounded-lg p-3 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-full">
            <Star className="w-4 h-4 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-primary-800 truncate">
                Welcome back, {data.guestName?.split(' ')[0] || guestName}!
              </span>
              {hasAllergies && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  <AlertTriangle className="w-3 h-3" />
                  Allergy
                </span>
              )}
            </div>
            <p className="text-xs text-primary-600">
              {totalVisits} visit{totalVisits !== 1 ? 's' : ''} - Tap for suggestions
            </p>
          </div>
          <ChevronDown className="w-5 h-5 text-primary-400" />
        </div>
      </button>
    );
  }

  return (
    <div className={clsx(
      'bg-gradient-to-br from-primary-50 via-white to-amber-50 border border-primary-100',
      compact ? 'rounded-lg' : 'rounded-xl'
    )}>
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="p-2.5 bg-primary-100 rounded-full">
          <Star className="w-5 h-5 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-primary-800">
              Welcome back, {data.guestName?.split(' ')[0] || guestName}!
            </h3>
            {(onClose || compact) && (
              <button
                onClick={onClose || (() => setExpanded(false))}
                className="p-1 -mr-1 text-primary-400 hover:text-primary-600 rounded"
              >
                {compact ? <ChevronUp className="w-5 h-5" /> : <X className="w-5 h-5" />}
              </button>
            )}
          </div>
          <p className="text-sm text-primary-600">
            {totalVisits} visit{totalVisits !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Allergy Alert */}
      {hasAllergies && (
        <div className="mx-4 mb-3 p-3 bg-amber-100 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">
              Allergy: {allergies.join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {hasSuggestions && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-medium text-primary-700">
              Based on {totalVisits} visit{totalVisits !== 1 ? 's' : ''}:
            </span>
          </div>
          <div className="space-y-2">
            {suggestions.slice(0, 3).map((suggestion: Suggestion) => (
              <div
                key={suggestion.menuItemId}
                className="flex items-center gap-3 p-2 bg-white/50 rounded-lg"
              >
                <Heart className="w-4 h-4 text-rose-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800 truncate block">
                    {suggestion.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {suggestion.reason || (suggestion.reasons && suggestion.reasons[0]) || 'Recommended for you'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Avoid Items */}
      {avoidItems.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <ThumbsDown className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-500">
              Avoid:
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {avoidItems.map((item: { menuItemId: string; name: string }) => (
              <span
                key={item.menuItemId}
                className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
              >
                {item.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasSuggestions && !hasAllergies && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-slate-500">
            No preferences recorded yet. Collect feedback after their visit!
          </p>
        </div>
      )}
    </div>
  );
}
